import type { AppState } from '../../state/appState.js';
import type {
  AddExistingInstanceCmd,
  AddFrameCmd,
  AddInstanceCmd,
  AddLayerCmd,
} from '../commandBuilders.js';
import type { FrameNode, SceneNodeId } from '../../scene/sceneState.js';
import type { ActionExecution } from '../commandExecutor.js';
import {
  cloneComponentDef,
  cloneLayerDef,
  getSceneTargetFor,
  insertSceneNode,
} from './shared.js';

export function execAddFrameFor(appState: AppState, cmd: AddFrameCmd): ActionExecution<FrameNode> {
  const target = getSceneTargetFor(appState, cmd.targetComponentId);
  const frame = insertSceneNode(target.scene, cmd.parentId, cmd.frame, cmd.index);
  return {
    result: frame,
    undoActions: [{ type: 'scene:removeFrame', nodeId: cmd.frame.id }],
    changed: true,
  };
}

export function execAddLayerFor(
  appState: AppState,
  cmd: AddLayerCmd,
): ActionExecution<{ nodeId: SceneNodeId; layerId: string }> {
  const target = getSceneTargetFor(appState, cmd.targetComponentId);
  appState.layerLibrary[cmd.layerDef.id] = cloneLayerDef(cmd.layerDef);
  insertSceneNode(target.scene, cmd.parentId, cmd.layer, cmd.index);
  return {
    result: { nodeId: cmd.layer.id, layerId: cmd.layer.layerId },
    undoActions: [{ type: 'scene:dropLayer', nodeId: cmd.layer.id, layerId: cmd.layer.layerId }],
    changed: true,
  };
}

export function execAddInstanceFor(
  appState: AppState,
  cmd: AddInstanceCmd,
): ActionExecution<{ instanceId: SceneNodeId; componentId: string }> {
  const target = getSceneTargetFor(appState, cmd.targetComponentId);
  appState.componentLibrary[cmd.component.id] = cloneComponentDef(cmd.component);
  appState.layerLibrary[cmd.layerDef.id] = cloneLayerDef(cmd.layerDef);
  insertSceneNode(target.scene, cmd.parentId, cmd.instance, cmd.index);

  return {
    result: { instanceId: cmd.instance.id, componentId: cmd.component.id },
    undoActions: [{ type: 'scene:dropOwnedInstance', nodeId: cmd.instance.id, componentId: cmd.component.id }],
    changed: true,
  };
}

export function execAddExistingInstanceFor(
  appState: AppState,
  cmd: AddExistingInstanceCmd,
): ActionExecution<{ instanceId: SceneNodeId; componentId: string }> {
  const target = getSceneTargetFor(appState, cmd.targetComponentId);
  insertSceneNode(target.scene, cmd.parentId, cmd.instance, cmd.index);
  return {
    result: { instanceId: cmd.instance.id, componentId: cmd.instance.componentId },
    undoActions: [{ type: 'scene:dropInstance', nodeId: cmd.instance.id }],
    changed: true,
  };
}
